/** Momentum strip: who scored in the last ~2 minutes of game time. Pure. */
import { OVERTIME_SECONDS, PERIOD_SECONDS, REGULATION_PERIODS, type GameEvent, type Side } from '@clutch/core';

/** Game seconds elapsed since tip-off at (period, clockSec). */
export function gameElapsed(period: number, clockSec: number): number {
  if (period <= REGULATION_PERIODS) return (period - 1) * PERIOD_SECONDS + (PERIOD_SECONDS - clockSec);
  return REGULATION_PERIODS * PERIOD_SECONDS + (period - REGULATION_PERIODS - 1) * OVERTIME_SECONDS + (OVERTIME_SECONDS - clockSec);
}

export type MomentumTick = { seq: number; side: Side; points: number; at: number }; // at: 0 (window start) → 1 (now)
export type Momentum = {
  windowSec: number;
  home: number;
  away: number;
  ticks: MomentumTick[];
  /** Unanswered points by one side ending at the latest score (≥ 5 to be shown). */
  run?: { side: Side; points: number };
  /** False when our play log starts inside the window (fan joined mid-window, no backfill). */
  complete: boolean;
};

const scoringSide = (e: GameEvent): Side | undefined =>
  (e.points ?? 0) > 0 && e.made !== false && (e.type === 'shot' || e.type === 'freethrow') ? e.side : undefined;

export function momentum(
  plays: readonly GameEvent[],
  now: { period: number; clockSec: number },
  opts: { windowSec?: number; logStartSeq?: number } = {},
): Momentum {
  const windowSec = opts.windowSec ?? 120;
  const end = gameElapsed(now.period, now.clockSec);
  const start = Math.max(0, end - windowSec);
  const out: Momentum = { windowSec, home: 0, away: 0, ticks: [], complete: true };

  const first = plays[0];
  if (first && first.seq > (opts.logStartSeq ?? 0) && gameElapsed(first.period, first.clockSec) > start) out.complete = false;
  if (!first) out.complete = end <= 0;

  for (const e of plays) {
    const side = scoringSide(e);
    if (!side) continue;
    const t = gameElapsed(e.period, e.clockSec);
    if (t < start || t > end) continue;
    out[side] += e.points!;
    out.ticks.push({ seq: e.seq, side, points: e.points!, at: windowSec ? (t - start) / (end - start || 1) : 1 });
  }

  // Run: walk back from the latest score while the same side keeps scoring.
  // Only claimed when the opponent's last score is in our log (or the log starts at tip-off).
  let run: { side: Side; points: number } | undefined;
  let closed = (first?.seq ?? 0) <= (opts.logStartSeq ?? 0);
  for (let i = plays.length - 1; i >= 0; i--) {
    const e = plays[i]!;
    const side = scoringSide(e);
    if (!side) continue;
    if (!run) run = { side, points: e.points! };
    else if (run.side === side) run.points += e.points!;
    else {
      closed = true;
      break;
    }
  }
  if (run && closed && run.points >= 5) out.run = run;
  return out;
}
